import { FormFieldDefinition, FormGroupDefinition, FormModel, FormNode, StringFieldDefinition } from '@msdk/definitions'

/**
 * Whether a form is long enough to need the validation panel. The panel exists to
 * recover errors the user has scrolled past, so it is noise on a form that fits the screen.
 */
export enum FormLength {
  Short = 'short',
  Long = 'long',
  Tabbed = 'tabbed',
}

/** A group spans 12 grid columns, so a full-width field is worth 12 units. */
export const FULL_ROW_UNITS = 12

/**
 * ~1200px of content at roughly 72px per rendered row, i.e. the two viewports of the
 * 1280x720 minimum supported resolution. Compared with `>`, so a form of exactly this
 * length still counts as short.
 */
export const LONG_FORM_FIELD_UNITS = 204

/** A lone tab is a single view, not a tabbed form. */
export const MIN_TABS = 2

const SENTINEL_COLUMNS = -1
const isTabLike = (node: FormNode) => node.value.type === 'Tab' || node.value.type === 'CustomTab'

/**
 * The column count a node lays its children out in. Groups declare it, the -1 sentinel
 * means two columns, and anything else unusable is treated as full width. Mirrors
 * Groups/utils.ts, which produces the widths the user actually sees.
 */
function colsOf(node: FormNode): number {
  const columns = node.value.type === 'Group' ? (node.value.formDefinition as FormGroupDefinition)?.columns : 0

  if (columns === SENTINEL_COLUMNS) {
    return 2
  }

  return columns || FULL_ROW_UNITS
}

/**
 * A field's weight is its share of a row: `columnSpan / columns`. A field with no declared
 * span occupies a single column, except a comment field, which is always full width.
 */
function unitsOf(node: FormNode, columns: number): number {
  const span = (node.value.formDefinition as FormFieldDefinition)?.columnSpan ?? 0
  if (span > 0) {
    return (span / columns) * FULL_ROW_UNITS
  }

  const field = node.value.fieldDefinition as StringFieldDefinition
  if (field?.fieldDefinitionType === 'String' && field.isComment) {
    return FULL_ROW_UNITS
  }

  return FULL_ROW_UNITS / columns
}

interface TabState {
  tabbed: boolean
}

/**
 * Sums a form's row units in one pass. A field is a leaf worth its share of a row; anything
 * else is a container whose children are summed in turn.
 *
 * Two shapes need special handling:
 *
 * - A tab run is worth its longest single tab, not their sum, because `Tabs` mounts only the
 *   active panel (packages/ui/src/components/Tabs/Tabs.tsx). Only a `Tab` counts towards the
 *   tabbed total: a `CustomTab` renders a federated component of unknown height, so a form
 *   built from those alone falls back to the length rule. Siblings are followed only while
 *   they stay tab-like, so a group after the run is still summed by the parent.
 * - A list repeats its cells per row, so its height follows the row count rather than the
 *   field count. Score the list itself once and never descend, or 30 rows read as 150 fields.
 */
function walk(node: FormNode | undefined, columns: number, state: TabState): number {
  if (!node || node.value.visibility !== 'Visible') {
    return 0
  }

  if (isTabLike(node)) {
    let visible = 0
    let longest = 0
    let tab: FormNode | undefined = node

    while (tab && isTabLike(tab)) {
      if (tab.value.visibility === 'Visible') {
        visible += tab.value.type === 'Tab' ? 1 : 0
        longest = Math.max(longest, walk(tab.firstChild, colsOf(tab), state))
      }
      tab = tab.nextSibling
    }

    if (visible >= MIN_TABS) {
      state.tabbed = true
    }

    return longest
  }

  if (node.value.type === 'List' || node.value.fieldDefinition) {
    return unitsOf(node, node.value.type === 'List' ? FULL_ROW_UNITS : columns)
  }

  const own = colsOf(node)
  let units = 0
  let child: FormNode | undefined = node.firstChild

  while (child) {
    units += walk(child, own, state)
    child = child.nextSibling
  }

  return units
}

export function getFormLength(root: FormNode, formModel?: FormModel): FormLength {
  /*
   * `LongForm` on the XML model is the author overruling the measurement, so it wins outright.
   * One-way by design: a form that omits the attribute, or sets it false, is still measured.
   */
  if (formModel?.longForm) {
    return FormLength.Long
  }

  const state: TabState = { tabbed: false }
  const units = walk(root, FULL_ROW_UNITS, state)

  if (state.tabbed) {
    return FormLength.Tabbed
  }

  return units > LONG_FORM_FIELD_UNITS ? FormLength.Long : FormLength.Short
}
